/**
 * Treatments — the vocabulary of surface renderings a card may take.
 *
 * Authorised by: `docs/RULINGS.md` R188 (founder decision), which permits a second theming axis
 * orthogonal to hue. design-direction §2 already gives Drystone *surface* — "colour, texture,
 * tone" — so texture has always sat inside the material remit; what R188 settles is that it
 * becomes a user choice rather than a constant, and on which side of the free/paid line it falls.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THERE IS NO TIER RECORD IN THIS FILE, AND ITS ABSENCE IS THE POINT
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * `materials.ts` carries `MATERIAL_TIER` because a material may be paid. A treatment may not, and
 * the reason is not generosity — it is that charging for one would retroactively make the four
 * paid materials illegal.
 *
 * `docs/DEVIATIONS.md` M11 permits a paid material on exactly one argument: a material changes
 * **hue and chroma only**, so it cannot reach any sensory control, and product-concept §12's
 * "sensory and accessibility settings are free forever, categorically" is therefore untouched. A
 * treatment breaks that premise — it adds visual noise to a surface, and for this audience visual
 * noise IS a sensory setting. The first paid thing that reaches texture destroys M11's argument
 * and takes Calch, Glo, Grug and Ffion down with it.
 *
 * So: free, for everyone, at every tier, forever. A future agent adding `TREATMENT_TIER` here
 * should read M11 first and understand that they are not pricing a new ornament, they are
 * repricing the four materials that already shipped.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * AND IT IS REACHABLE BY THE DIAL, WHICH IS THE OTHER HALF OF THE SAME ARGUMENT
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * A free setting that a sensory-sensitive user cannot turn *off* is not an accommodation. Two
 * mechanisms guarantee they can, and `@carn/tokens`' `resolveField` enforces both:
 *
 *   1. `matte` is the default and is the rendering that shipped before treatments existed.
 *   2. The `decor` channel zeroes the field at level 0 whatever this setting says — so the
 *      low-stim reference rendering (design-direction §3 objection 2, CLAUDE.md) is untouched by
 *      construction, not by the user remembering to come back here and undo their choice.
 */

/**
 * The treatments, in the fixed order they are offered.
 *
 * `matte` is first because it is the default and the reference rendering, and the order after it
 * runs least → most visually present, which is the order `Settings.density` and the Intensity
 * dial both use. design-direction §4 fixes spatial grammar; a picker whose order means something
 * is the same instinct applied to a list.
 */
export const TREATMENT_IDS = ['matte', 'smooth', 'contour', 'posterise', 'dither'] as const;
export type Treatment = (typeof TREATMENT_IDS)[number];

/**
 * The default, and the rendering that shipped before this axis existed.
 *
 * `packages/tokens/test/field.test.ts` asserts that resolving any theme at `matte` produces a
 * field with no geometry at all, which is the same guarantee `materials.test.ts` gives Drystone:
 * the new axis is a generalisation of what already shipped, not a replacement for it.
 */
export const DEFAULT_TREATMENT: Treatment = 'matte';

export function isTreatment(value: unknown): value is Treatment {
  return typeof value === 'string' && (TREATMENT_IDS as readonly string[]).includes(value);
}

/**
 * Clamp any stored or synced value back to a renderable treatment.
 *
 * The counterpart of `normaliseMaterial` and `normaliseIntensity`, for the same stated reason: a
 * value arriving from another device or a future app version must never produce an unrenderable
 * theme. An unknown id resolves to the default rather than throwing — a phone that syncs a
 * treatment it has never heard of should render `matte`, not fail to draw.
 */
export function normaliseTreatment(input: unknown): Treatment {
  return isTreatment(input) ? input : DEFAULT_TREATMENT;
}
